import { Component, h, Prop, Event, EventEmitter, State, Element } from "@stencil/core";

@Component({
  tag: "datagrid-filter-checkbox-eligible-year",
  shadow: false,
})
export class DatagridFilterEligibleYear {
  @Element() el!: HTMLElement;
  /**
   *  The ID of the grid to which this filter belongs.
   *  This is used to identify which grid the filter is associated with.
   *  It is passed to the filterChanged event when the filter value changes.
   *  @example "grid-1"
   *  @type {string}
   *  @required
   *  @description The ID of the grid to which this filter belongs.
   *  This is used to identify which grid the filter is associated with.
   *  It is passed to the filterChanged event when the filter value changes.
   *  @default ""
   */
  @Prop() grid_id!: string;
  /**
   * The field name to which this filter is applied.
   * This is used to identify which field the filter is associated with.
   * It is passed to the filterChanged event when the filter value changes.
   * @example "eligible_year"
   * @type {string}
   * @required
   * @description The field name to which this filter is applied.
   * This is used to identify which field the filter is associated with.
   * It is passed to the filterChanged event when the filter value changes.
   * @default ""
   */
  @Prop() field!: string;
  /**
   * The label to be displayed for this filter.
   * This is used to identify the filter in the UI.
   * @example "Eligible Year"
   * @type {string}
   * @required
   * @description The label to be displayed for this filter.
   * This is used to identify the filter in the UI.
   * @default ""
   */
  @Prop() label!: string;
  /**
   * The options to be displayed in the filter.
   * This is a JSON string that contains an array of objects with label and value properties.
   * @example '[{"label": "2021", "value": "2021"}, {"label": "2022", "value": "2022"}]'
   */
  @Prop() options!: string;
  /**
   * The initial value of the filter.
   * This is used to set the initial state of the filter when it is first rendered.

   */
  @Prop() initialValue?: string;
  /**
   * The type of the filter.
   * This is used to identify the type of filter in the UI.
   */
  @Event() filterChanged: EventEmitter<{
    grid_id: string;
    field: string;
    value: any;
    type: string;
  }>;

  @State() selectedValues: string[] = [];
  @State() selectedValue: any = "";

  handleCheckboxChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const value = target.value;

    let updatedValues = [...this.selectedValues];

    if (target.checked) {
      updatedValues.push(value);
    } else {
      updatedValues = updatedValues.filter((v) => v !== value);
    }

    this.selectedValues = updatedValues;
    this.emitFilterChange();
    // Vue 2: emit 'input' event
    this.el.dispatchEvent(new CustomEvent('input', {
      bubbles: true,
      composed: true,
      detail: this.selectedValues,
    }));

    // Vue 3: emit 'update:modelValue' event
    this.el.dispatchEvent(new CustomEvent('update:modelValue', {
      bubbles: true,
      composed: true,
      detail: this.selectedValues,
    }));
  }

  emitFilterChange() {
    this.filterChanged.emit({
      grid_id: this.grid_id,
      field: this.field,
      value: this.selectedValues,
      type: "datagrid-filter-checkbox-eligible-year",
    });
  }

  get parsedOptions(): { label: string; value: string }[] {
    try {
      const parsed = this.options ? JSON.parse(this.options) : [];
      if (Array.isArray(parsed)) {
        return parsed.map((opt) =>
          typeof opt === "string" ? { label: opt, value: opt } : opt
        );
      }
      return [];
    } catch (e) {
      console.error("🚨 Error parsing options:", this.options, e);
      return [];
    }
  }

  render() {
    return (
      <div class="datagrid-filter-checkbox-eligible-year">
        <div class="checkbox-filter">
          {this.parsedOptions.map((option) => (
            <div class="checkbox-item" style={{ marginBottom: "10px" }}>
              <signet-checkbox
                label={option.label}
                value={option.value}
                checked={this.selectedValues.includes(option.value)}
                onChange={(e) => this.handleCheckboxChange(e)}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }
}
